import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BubbleDirective } from '@ui/aos-lib';
import { DeviceSidePanelComponent } from '../../../devices/components/device-side-panel/device-side-panel.component';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { DeviceDetails } from '../../../devices/components/devices.models';
import { UnitPipe } from 'src/app/shared/pipes/unit.pipe';
import {
  OnDemandReadConvertedDto,
  DataRecoveredDto,
  TwoWayRequestItemListResponseDto,
  TwoWayRequestProcessingCommandDetailDto,
  TwoWayRequestProcessingCommandDetailKey
} from '../../services/request-processing.types';
import { DeviceConfigRequestStatus } from '../../../devices/constants/devices.constants';
import { commandStatusBubbleMap, commandStatusLabelMap } from '../command-status.constants';

type CommandDetailItem = {
  titleKey: string;
  value: string;
  bubbleValue?: string;
};

type CommandDetailRow = {
  left: CommandDetailItem;
  right?: CommandDetailItem;
};

const commandDetailLabelKeyMap: Record<TwoWayRequestProcessingCommandDetailKey, string> = {
  [TwoWayRequestProcessingCommandDetailKey.BoardType]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.BOARD_TYPE,
  [TwoWayRequestProcessingCommandDetailKey.Position]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.POSITION,
  [TwoWayRequestProcessingCommandDetailKey.BoardSerialNumber]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.BOARD_SERIAL_NUMBER,
  [TwoWayRequestProcessingCommandDetailKey.FirmwareVersion]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.FIRMWARE_VERSION,
  [TwoWayRequestProcessingCommandDetailKey.MeterRead]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.METER_READ,
  [TwoWayRequestProcessingCommandDetailKey.DataRecoveredRead]:
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.RECOVERED_READS
};

@Component({
  selector: 'aos-app-request-processing-device-side-panel',
  imports: [TranslateModule, DeviceSidePanelComponent, BubbleDirective],
  templateUrl: './request-processing-device-side-panel.component.html',
  styleUrls: ['./request-processing-device-side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestProcessingDeviceSidePanelComponent {
  readonly basicData = input<TwoWayRequestItemListResponseDto>();
  readonly customerCardData = input<unknown | null>();
  readonly isMap = input(false);
  readonly deviceCardData = input.required<readonly DeviceDetails[]>();

  private readonly unitPipe = new UnitPipe();
  private readonly translate = inject(TranslateService);

  protected readonly commandTitle = computed(() => String(this.basicData()?.command ?? ''));
  protected readonly commandStatus = computed(() => String(this.basicData()?.commandStatus ?? ''));
  protected readonly commandStatusLabelMap = commandStatusLabelMap;
  protected readonly commandStatusBubbleMap = commandStatusBubbleMap;

  protected readonly commandDetailItems = computed<readonly CommandDetailItem[]>(() => {
    const items: CommandDetailItem[] = [];
    const statusRaw = this.commandStatus();
    const status = this.getCommandStatus(statusRaw);

    // Always show Command Status first.
    items.push({
      titleKey: this.commandStatusTitle,
      value: status ? commandStatusLabelMap[status] || statusRaw : statusRaw,
      bubbleValue: status ? commandStatusBubbleMap[status] : undefined
    });

    const commandDetails: readonly TwoWayRequestProcessingCommandDetailDto[] = this.basicData()?.commandDetails ?? [];
    const isMeterRead = (detail: TwoWayRequestProcessingCommandDetailDto): boolean => {
      const key = String(detail?.labelKey ?? '');
      return key.toLowerCase() === 'meterread';
    };

    const isRecoveredRead = (
      detail: TwoWayRequestProcessingCommandDetailDto
    ): detail is TwoWayRequestProcessingCommandDetailDto & { value: DataRecoveredDto } => {
      return (
        typeof detail.value === 'object' &&
        detail.value !== null &&
        'expectedReads' in detail.value &&
        'recoveredReads' in detail.value
      );
    };

    const shouldIncludeCommandDetail = (detail: TwoWayRequestProcessingCommandDetailDto): boolean => {
      const hasLabelKey = Boolean(detail?.labelKey);
      const hasValue = detail?.value !== null && detail?.value !== undefined;

      return hasLabelKey && hasValue;
    };

    // Append extra command details.
    commandDetails.filter(shouldIncludeCommandDetail).forEach(d => {
      if (isMeterRead(d)) {
        const converted = d.value as OnDemandReadConvertedDto;
        items.push({
          titleKey: TranslateRegistry.get.ON_DEMAND_READ.TITLE.METER_READ,
          value: this.unitPipe.transform(
            converted?.value,
            converted?.uom ?? '',
            true,
            converted?.multiplier == null ? '' : String(converted.multiplier)
          )
        });
        return;
      }

      if (isRecoveredRead(d)) {
        const converted = d.value;
        items.push({
          titleKey: this.resolveCommandDetailTitleKey(String(d.labelKey)),
          value:
            String(converted?.recoveredReads ?? 'N/A') +
            (converted?.expectedReads != null
              ? ` ${this.translate.instant(TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.OF)} ${converted.expectedReads}`
              : '')
        });
        return;
      }

      items.push({
        titleKey: this.resolveCommandDetailTitleKey(String(d.labelKey)),
        value: d.value == null ? '' : String(d.value)
      });
    });

    return items;
  });

  protected readonly commandDetailRows = computed<readonly CommandDetailRow[]>(() => {
    const items = this.commandDetailItems();
    const rows: CommandDetailRow[] = [];
    for (let index = 0; index < items.length; index += 2) {
      rows.push({
        left: items[index],
        right: items[index + 1]
      });
    }
    return rows;
  });

  protected readonly commandDetailsTitle = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.COMMAND_DETAILS;
  protected readonly commandStatusTitle = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.COMMAND_STATUS;
  protected readonly deviceTitle = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.SIDE_PANEL.DEVICE;

  protected resolveCommandDetailTitleKey(labelKey: string): string {
    return commandDetailLabelKeyMap[labelKey as TwoWayRequestProcessingCommandDetailKey] ?? labelKey;
  }

  protected getCommandStatus(
    status: TwoWayRequestItemListResponseDto['commandStatus']
  ): DeviceConfigRequestStatus | null {
    return Object.values(DeviceConfigRequestStatus).includes(status as DeviceConfigRequestStatus)
      ? (status as DeviceConfigRequestStatus)
      : null;
  }
}
